import { DENOMINATIONS_CENTS } from '../config.js';
import type { SvgViewBox } from './zones.js';

export type SvgKey = 'tableMain' | 'tableHardway';

export interface SvgPanel {
  svgKey: SvgKey;
  /** Where to draw the panel on the canvas, in canvas-buffer px. */
  dst: { x: number; y: number; w: number; h: number };
  /** Source-SVG viewBox for this panel (used by zone-building math). */
  viewBox: SvgViewBox;
}

export interface Rect { x: number; y: number; w: number; h: number; }

export type PlayerCardLayout =
  | { mode: 'card' }
  | {
      mode: 'cardVertical';
      cardRect: Rect;
      historyRect: Rect;
    };

export interface TableLayout {
  panels: SvgPanel[];
  chipRack: { x: number; y: number; scale: number };
  dice:     { x: number; y: number; size: number };
  puck:     { x: number; y: number; scale: number };
  playerCard: PlayerCardLayout;
  /**
   * Rectangle reserved for the ATS bet strip (Small / All / Tall).
   * - Portrait: tall column to the right of the hardway in the top row
   *   (taller than wide → strip builder lays cells out vertically).
   * - Landscape: 104-SVG-unit slab above the hardway (wider than tall →
   *   cells are laid out horizontally instead).
   */
  atsStripRect?: Rect;
}

export const MAIN_VIEWBOX:    SvgViewBox = { x: 0,       y: 0,   w: 476.25, h: 330 };
export const HARDWAY_VIEWBOX: SvgViewBox = { x: 509.375, y: 149, w: 226, h: 226 };

/**
 * Landscape factory — produces a TableLayout that reproduces the original
 * single-SVG layout pixel-for-pixel using the two new SVG slices.
 *
 * Hardway is shifted up so its bottom edge aligns with the main panel's
 * bottom (main is 330 SVG-units tall, hardway 226 → top at 330-226=104).
 * The overall table is centered between two reserves: a top band that
 * clears the FLOOR / ? / history pills, and a bottom band sized to fit the
 * dice (above) plus chip rack (below) without colliding. Empty canvas
 * regions display TABLE_BG (matches the SVG felt fill #0d3b2a exactly).
 * tableHardway is drawn after tableMain so the overlap region renders
 * correctly.
 */
export function landscapeLayout(W: number, H: number): TableLayout {
  const sm = Math.min(W / 1280, H / 800);
  const pad = Math.max(8, Math.round(8 * sm));
  const diceSize = Math.round(64 * sm);
  const chipScale = Math.max(1, Math.min(2, W / 520));
  const chipR = 24 * chipScale;

  // Top reserve: history sits at y≈78 CSS-px below the FLOOR + ? pills, ~12
  // CSS-px tall. 100 * sm buffer-px gives ~50 CSS-px of clearance which on
  // landscape DPR (sm ≈ dpr) lands cleanly below the history strip.
  const topReserve = Math.round(100 * sm);
  // Bottom reserve: dice (top of bottom strip) + chip rack (bottom edge) +
  // padding between them and below the chip rack. Mirrors the portrait math
  // so neither rows collide with each other or with the felt.
  const bottomReserve = diceSize + chipR * 2 + pad * 3;

  // Post bottom-align, the visible felt is 800 wide × 330 tall in SVG units.
  const SVG_W = 800;
  const SVG_H = 330;
  const availH = H - topReserve - bottomReserve;
  const scale = Math.min(W / SVG_W, availH / SVG_H);
  const scaledW = SVG_W * scale;
  const scaledH = SVG_H * scale;
  const offsetX = (W - scaledW) / 2;
  const offsetY = topReserve + Math.max(0, (availH - scaledH) / 2);

  // Bottom strip: dice hugs the top, chip rack hugs the bottom (mirrors the
  // portrait layout's bottom-strip pattern).
  const bottomY = offsetY + scaledH + pad;
  const diceY = bottomY + Math.round(diceSize / 2);

  const chipRackSpacing = 70 * chipScale;
  const chipRackHalfSpan = ((DENOMINATIONS_CENTS.length - 1) * chipRackSpacing) / 2;
  const chipRackX = Math.round(W / 2 - chipRackHalfSpan);
  const chipRackY = H - chipR - pad;

  // ATS strip — fits in the empty slab above the hardway (hardway is bottom-
  // aligned with main, so its top 104 SVG-units of column are blank felt).
  // Cells are laid out horizontally inside this rect by TableScene's strip
  // builder; the shape (wider than tall) is what tells the builder to walk
  // an X-cursor instead of Y.
  const atsStripRect: Rect = {
    x: offsetX + 509.375 * scale,
    y: offsetY,
    w: 226 * scale,
    h: (330 - 226) * scale,
  };

  return {
    panels: [
      {
        svgKey: 'tableMain',
        dst: {
          x: offsetX,
          y: offsetY,
          w: 476.25 * scale,
          h: 330 * scale,
        },
        viewBox: MAIN_VIEWBOX,
      },
      {
        svgKey: 'tableHardway',
        dst: {
          x: offsetX + 509.375 * scale,
          y: offsetY + (330 - 226) * scale,
          w: 226 * scale,
          h: 226 * scale,
        },
        viewBox: HARDWAY_VIEWBOX,
      },
    ],
    chipRack: { x: chipRackX, y: chipRackY, scale: chipScale },
    dice:     { x: W / 2,     y: diceY,     size: diceSize },
    // OFF puck rests inside the don't-come wedge (SVG ~82, ~50) — left side
    // of the table, just above where don't-come chips would sit. Matches the
    // real-craps convention where the dealer parks the puck in the don't-come
    // box during come-out.
    puck:     {
      x: offsetX + 82 * scale,
      y: offsetY + 50 * scale,
      scale: sm,
    },
    playerCard: { mode: 'card' },
    atsStripRect,
  };
}

/**
 * Portrait factory — stacks the hardway panel on top (with a vertical
 * player card filling the right margin) and the main panel below, with a
 * chips/dice strip at the bottom and a thin history strip across the very
 * top of the canvas. All sizing scales from canvas dims so phones from
 * ~320 wide up through tablet portrait sit in budget.
 */
export function portraitLayout(W: number, H: number): TableLayout {
  const pad = Math.round(H * 0.02);

  // History strip removed (kept the rect for layout call sites that still
  // reference it; the UIScene no longer renders it). FLOOR + help pills sit
  // at viewport-px (12, 14) and (12, 46), ending at ~70 CSS-px. floorReserve
  // pushes the felt below them with a small gap.
  const historyH = 0;
  const floorReserve = Math.round(H * 0.06);
  const historyY = pad + floorReserve;
  const historyX = Math.round(W * 0.04);
  const historyRect = {
    x: historyX,
    y: historyY,
    w: W - 2 * historyX,
    h: historyH,
  };

  // Top row (hardway + ATS) sits just below the FLOOR row now. Main panel
  // sits one pad below the hardway.
  const topRowH = Math.round(H * 0.30);
  const topRowY = historyY + historyH + pad;

  // Hardway is flush-left in the top row; the ATS strip on the right is
  // flush-right. Both have an outer `pad` from the canvas edge with the
  // gap between them sized so hardway + gap + atsStripRect together span
  // the row's full usable width. This reads as a justified pair across
  // the top instead of "centered hardway with a thin strip wedged on the
  // right and matching dead air on the left".
  const hardwaySize = Math.min(topRowH, Math.round(W * 0.55));

  // Three visible top-row margins — canvas → hardway, hardway → ATS, ATS
  // → canvas — must come out equal for the row to read as a balanced
  // pair. To pull this off, the ATS cell is sized to its visible content
  // width (square + two number columns + side gaps), eliminating the
  // dead cell space that previously bloated the right margin. The
  // remaining horizontal slack is then divided three ways.
  //
  // The constants below mirror AtsBetZone's internal layout math; if you
  // change one side you have to change the other or the cell + content
  // will drift out of sync and the dead space comes back.
  const atsZoneGap   = 4;
  const atsAllCellH  = Math.floor((hardwaySize - 2 * atsZoneGap) * 0.40);
  const atsFontSize  = Math.max(12, Math.round((atsAllCellH / 5) * 0.7));
  const atsTargetW   = Math.round(atsFontSize * 2.5);
  // +5 buffer-px breathing room between number columns and the box,
  // matching AtsBetZone's sideGap formula. Both sides must stay in sync.
  const atsSideGap   = Math.round(atsFontSize * 0.6) + 5;
  const atsNumHalfW  = Math.round(atsFontSize * 0.55);
  const atsContentW  = atsTargetW + 2 * atsSideGap + 2 * atsNumHalfW;
  const topRowMargin = Math.round((W - hardwaySize - atsContentW) / 3);

  const hardwayX = topRowMargin;
  const hardwayY = topRowY;
  const mainY = hardwayY + hardwaySize + pad;

  const cardX = topRowMargin * 2 + hardwaySize;
  const cardY = topRowY;
  const cardW = atsContentW;
  const cardH = hardwaySize;
  const cardRect = { x: cardX, y: cardY, w: cardW, h: cardH };

  // Main panel: pin the visible felt outline (PASS LINE outer edge sits at
  // SVG x=25 — the viewBox has ~25 units of empty felt-coloured slab before
  // the white outlines start) to ~5px from each canvas side. The dst rect
  // still spans the full SVG box so hit areas and chip anchors stay in their
  // natural SVG coords; the empty slab on the left simply clips off-canvas
  // and the canvas TABLE_BG (same colour) fills the gap.
  const SVG_LEFT_PAD = 25;
  const SVG_FULL_W = 476.25;
  const SVG_FULL_H = 330;
  const SVG_VISIBLE_W = SVG_FULL_W - SVG_LEFT_PAD;
  // Match the top-of-canvas pad so the visible felt outline has the same
  // gutter on every side it can.
  const mainPadX = pad;
  const scaleByWidth = (W - 2 * mainPadX) / SVG_VISIBLE_W;

  // Reserve enough room below the main panel so the dice (drawn at the top
  // of the bottom strip) and the chip rack (drawn at its bottom) don't
  // overlap. On viewports where the address bar eats into H (Safari with
  // chrome visible, iPhone SE), the width-driven scale would leave too
  // little room — cap by height so the table shrinks instead.
  const diceSize = Math.max(64, Math.round(W * 0.12));
  const chipScale = Math.max(1, Math.min(2, W / 520));
  const chipR = 24 * chipScale;
  const bottomReserve = diceSize + chipR * 2 + pad * 3;
  const scaleByHeight = (H - mainY - bottomReserve) / SVG_FULL_H;
  const scale = Math.min(scaleByWidth, scaleByHeight);

  const visibleW = SVG_VISIBLE_W * scale;
  const mainX = (W - visibleW) / 2 - SVG_LEFT_PAD * scale;
  const mainW = SVG_FULL_W * scale;
  const mainHActual = Math.round(SVG_FULL_H * scale);

  // Bottom strip: positioned right after the main panel (no dead space).
  const bottomY = mainY + mainHActual + pad;
  const bottomH = H - bottomY - pad;

  // Dice hugs the top of the bottom strip (just under the table); chip rack
  // hugs the bottom (closest to the player's thumbs on a phone). Pinning to
  // edges instead of percentages keeps the dice/chips clear of each other
  // even when the strip is short. Rack first-chip-X compensates for the
  // 70*scale spacing between denominations so the rack reads centered.
  const diceX = Math.round(W / 2);
  const diceY = bottomY + Math.round(diceSize / 2);

  const chipRackSpacing = 70 * chipScale;
  const chipRackHalfSpan = ((DENOMINATIONS_CENTS.length - 1) * chipRackSpacing) / 2;
  const chipRackX = Math.round(W / 2 - chipRackHalfSpan);
  const chipRackY = bottomY + bottomH - Math.round(chipR);

  // OFF puck rests inside the don't-come wedge (SVG ~82, ~50) — left side
  // of the table, just above where don't-come chips would sit. Matches the
  // real-craps convention where the dealer parks the puck in the don't-come
  // box during come-out.
  const puckScale = scale;
  const puckX = mainX + 82 * puckScale;
  const puckY = mainY + 50 * puckScale;

  return {
    panels: [
      {
        svgKey: 'tableHardway',
        dst: { x: hardwayX, y: hardwayY, w: hardwaySize, h: hardwaySize },
        viewBox: HARDWAY_VIEWBOX,
      },
      {
        svgKey: 'tableMain',
        dst: { x: mainX, y: mainY, w: mainW, h: mainHActual },
        viewBox: MAIN_VIEWBOX,
      },
    ],
    chipRack: { x: chipRackX, y: chipRackY, scale: chipScale },
    dice:     { x: diceX,     y: diceY,     size: diceSize },
    puck:     { x: puckX,     y: puckY,     scale: puckScale },
    playerCard: {
      mode: 'cardVertical',
      cardRect,
      historyRect,
    },
    atsStripRect: cardRect,
  };
}
